import React, {CSSProperties, HTMLAttributes, useMemo} from 'react'
import {cn} from '@containers/html-utils'

type GridValue = number | string
type GridGap = number | string

export interface SharedGridProps extends HTMLAttributes<HTMLDivElement> {
  // Grid template
  columns?: GridValue
  rows?: GridValue
  // Gap
  gap?: GridGap
  rowGap?: GridGap
  columnGap?: GridGap
  // Grid items placement
  justifyItems?: 'start' | 'end' | 'center' | 'stretch'
  alignItems?: 'start' | 'end' | 'center' | 'stretch'
  // Grid container alignment
  justifyContent?:
    | 'start'
    | 'end'
    | 'center'
    | 'stretch'
    | 'space-around'
    | 'space-between'
    | 'space-evenly'
  alignContent?:
    | 'start'
    | 'end'
    | 'center'
    | 'stretch'
    | 'space-around'
    | 'space-between'
    | 'space-evenly'
  // Auto flow
  autoFlow?: 'row' | 'column' | 'row dense' | 'column dense'
  // Auto columns/rows
  autoColumns?: 'auto' | 'min-content' | 'max-content' | GridValue
  autoRows?: 'auto' | 'min-content' | 'max-content' | GridValue
  // Inline grid
  inline?: boolean
  // Areas
  areas?: string
  // Additional styles and classnames
  className?: string
  style?: CSSProperties
  // Children
  children?: React.ReactNode
}

const resolveCssValue = (value: GridValue): string => {
  if (typeof value === 'number') {
    return `${value}px`
  }

  // If it's already a valid grid template value like "1fr 2fr" or "repeat(3, 1fr)" return as is
  return value
}

export const SharedGrid: React.FC<SharedGridProps> = props => {
  const {
    columns,
    rows,
    gap,
    rowGap,
    columnGap,
    justifyItems,
    alignItems,
    justifyContent,
    alignContent,
    autoFlow,
    autoColumns,
    autoRows,
    inline,
    areas,
    className,
    style,
    children,
    ...rest
  } = props

  const gridStyle = useMemo(() => {
    const computedStyle: CSSProperties = {...style}

    if (columns) {
      computedStyle.gridTemplateColumns = resolveCssValue(columns)
    }

    if (rows) {
      computedStyle.gridTemplateRows = resolveCssValue(rows)
    }

    if (gap !== undefined) {
      computedStyle.gap = typeof gap === 'number' ? `${gap}px` : gap
    }

    if (rowGap !== undefined) {
      computedStyle.rowGap = typeof rowGap === 'number' ? `${rowGap}px` : rowGap
    }

    if (columnGap !== undefined) {
      computedStyle.columnGap =
        typeof columnGap === 'number' ? `${columnGap}px` : columnGap
    }

    if (justifyItems) {
      computedStyle.justifyItems = justifyItems
    }

    if (alignItems) {
      computedStyle.alignItems = alignItems
    }

    if (justifyContent) {
      computedStyle.justifyContent = justifyContent
    }

    if (alignContent) {
      computedStyle.alignContent = alignContent
    }

    if (autoFlow) {
      computedStyle.gridAutoFlow = autoFlow
    }

    if (autoColumns) {
      computedStyle.gridAutoColumns =
        typeof autoColumns === 'number' ? `${autoColumns}px` : autoColumns
    }

    if (autoRows) {
      computedStyle.gridAutoRows =
        typeof autoRows === 'number' ? `${autoRows}px` : autoRows
    }

    if (areas) {
      computedStyle.gridTemplateAreas = areas
    }

    return computedStyle
  }, [
    columns,
    rows,
    gap,
    rowGap,
    columnGap,
    justifyItems,
    alignItems,
    justifyContent,
    alignContent,
    autoFlow,
    autoColumns,
    autoRows,
    areas,
    style
  ])

  return (
    <div
      className={cn(inline ? 'inline-grid' : 'grid', className)}
      style={gridStyle}
      {...rest}
    >
      {children}
    </div>
  )
}
